import { useEffect } from "react";

/**
 * useSeo - lightweight document head manager.
 * Sets <title>, meta description, canonical, Open Graph tags and optional
 * JSON-LD structured data per page. Restores the previous values on unmount.
 */
const SITE_NAME = "UPPIT";
// SITE_BASE = the canonical production origin used for canonical/OG URLs.
// Defaults to uppit.nl (the intended production domain) but can be overridden
// at build-time via REACT_APP_SITE_URL for staging or alternative deployments.
const SITE_BASE = (process.env.REACT_APP_SITE_URL || "https://uppit.nl").replace(/\/$/, "");
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const DEFAULT_OG_IMAGE = `${BACKEND_URL}/api/og/page/home.png`;

const setMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [, type, key] = selector.match(/meta\[(name|property)="([^"]+)"\]/) || [];
    if (type && key) el.setAttribute(type, key);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  let el = document.head.querySelector(`script[data-seo="${id}"]`);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

export const useSeo = ({
  title,
  description,
  path = "/",
  image,
  ogSlug,
  jsonLd = null,
  noindex = false,
}) => {
  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const url = `${SITE_BASE}${path}`;
    const ogImage = image || (ogSlug ? `${BACKEND_URL}/api/og/${ogSlug}.png` : DEFAULT_OG_IMAGE);

    document.title = fullTitle;
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[name="robots"]', "content", noindex ? "noindex, nofollow" : "index, follow");
    setLink("canonical", url);

    setMeta('meta[property="og:locale"]', "content", "nl_NL");
    setMeta('meta[property="og:type"]', "content", "website");
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="og:site_name"]', "content", SITE_NAME);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[property="og:image:width"]', "content", "1200");
    setMeta('meta[property="og:image:height"]', "content", "630");

    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", ogImage);

    setJsonLd("page-jsonld", jsonLd);

    // Scroll to top on navigation (unless URL has hash)
    if (!window.location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [title, description, path, image, ogSlug, jsonLd, noindex]);
};

export default useSeo;
